<template>
  <ul
    data-testid="pagination"
    class="b-pagination pagination"
    :class="[styleClass, btnSize, alignment, `pagination--${variant}`]"
    :aria-disabled="disabled || 'false'"
    :aria-label="ariaLabel"
    role="menubar"
  >
    <li
      v-if="hideGotoEndButtons || !lastNumber"
      class="page-item"
      role="presentation"
      :class="{ disabled : disabled || currentPage === 1, [firstClass]: firstClass}"
      :aria-disabled="currentPage === 1 || 'false'"
    >
      <button
        class="page-link"
        role="menuitem"
        type="button"
        tabindex="-1"
        :aria-label="labelFirstPage"
        @click="onClick($event, 1)"
      >
        {{ firstText }}
      </button>
    </li>
    <li
      class="page-item"
      role="presentation"
      :class="{ disabled : disabled || currentPage === 1, [prevClass]: prevClass}"
      :aria-disabled="currentPage === 1 || 'false'"
    >
      <button
        class="page-link"
        role="menuitem"
        type="button"
        :aria-label="labelPrevPage"
        tabindex="-1"
        @click="onClick($event, currentPage - 1)"
      >
        {{ prevText }}
      </button>
    </li>

    <li
      v-if="firstNumber"
      class="page-item"
      :class="{active: currentPage === 1, [pageClass]: pageClass, disabled}"
      role="presentation"
    >
      <button
        v-if="currentPage !==1 && limit - currentPage < 1"
        class="page-link"
        role="menuitem"
        type="button"
        :aria-label="`${labelPage} ${firstNumber}`"
        :aria-checked="currentPage === 1 || 'false'"
        aria-posinset="1"
        :aria-setsize="localNumberOfPages"
        tabindex="-1"
        @click="onClick($event, 1)"
      >
        <span
          v-if="currentPage === 1 && loading"
          class="spinner-border spinner-border-sm text-light"
          role="status"
        />
        <template v-else>
          1
        </template>
      </button>
    </li>

    <li
      v-if="showFirstDots && !hideEllipsis"
      class="page-item"
      :class="ellipsisClass"
      role="separator"
    >
      <span>
        {{ ellipsisText }}
      </span>
    </li>
    <li
      v-for="page in pageList"
      :key="page.number"
      class="page-item"
      :class="{active: page.number === currentPage, [pageClass]: pageClass, disabled}"
      role="presentation"
    >
      <button
        class="page-link"
        role="menuitem"
        type="button"
        :aria-label="`${labelPage} ${page.number}`"
        :aria-checked="page.number === currentPage || 'false'"
        :aria-posinset="page.number"
        :aria-setsize="localNumberOfPages"
        tabindex="-1"
        @click="onClick($event, page.number)"
      >
        <span
          v-if="page.number === currentPage && loading"
          class="spinner-border spinner-border-sm text-light"
          role="status"
        />
        <template v-else>
          {{ page.number }}
        </template>
      </button>
    </li>
    <li
      v-if="showLastDots && !hideEllipsis"
      class="page-item"
      role="separator"
      :class="ellipsisClass"
    >
      <span>
        {{ ellipsisText }}
      </span>
    </li>

    <li
      v-if="lastNumber"
      class="page-item"
      :class="{active: currentPage === localNumberOfPages, [pageClass]: pageClass, disabled}"
      role="presentation"
    >
      <button
        v-if="currentPage !== localNumberOfPages && localNumberOfPages - pageList[0].number >= limit"
        class="page-link"
        role="menuitem"
        type="button"
        :aria-label="`${labelPage} ${lastNumber}`"
        :aria-checked="currentPage === localNumberOfPages || 'false'"
        :aria-posinset="localNumberOfPages"
        :aria-setsize="localNumberOfPages"
        tabindex="-1"
        @click="onClick($event, localNumberOfPages)"
      >
        <span
          v-if="currentPage === localNumberOfPages && loading"
          class="spinner-border spinner-border-sm text-light"
          role="status"
        />
        <template v-else>
          {{ localNumberOfPages }}
        </template>
      </button>
    </li>

    <li
      class="page-item"
      role="presentation"
      :class="{ disabled : disabled || currentPage === localNumberOfPages, [nextClass]: nextClass}"
      :aria-hidden="currentPage === localNumberOfPages || 'false'"
    >
      <button
        class="page-link"
        role="menuitem"
        type="button"
        :arial-label="labelNextPage"
        tabindex="-1"
        @click="onClick($event, currentPage + 1)"
      >
        {{ nextText }}
      </button>
    </li>
    <li
      v-if="hideGotoEndButtons || !lastNumber"
      class="page-item"
      role="presentation"
      :class="{ disabled : currentPage === localNumberOfPages, [lastClass]: lastClass}"
      :aria-hidden="currentPage === localNumberOfPages || 'false'"
    >
      <button
        class="page-link"
        role="menuitem"
        type="button"
        tabindex="-1"
        :aria-label="labelLastPage"
        @click="onClick($event, localNumberOfPages)"
      >
        {{ lastText }}
      </button>
    </li>
  </ul>
</template>

<script>
const ELLIPSIS_THRESHOLD = 3
const DEFAULT_LIMIT = 5
const DEFAULT_PER_PAGE = 20
const DEFAULT_TOTAL_ROWS = 0

const VALID_VARIANTS = {
  dark: 'dark',
  light: 'light'
}

const sanitizeLimit = value => {
  const limit = parseInt(value)
  return limit < 1 ? DEFAULT_LIMIT : limit
}

const sanitizeCurrentPage = (val, numberOfPages) => {
  const page = parseInt(val) || 1
  return page > numberOfPages ? numberOfPages : page < 1 ? 1 : page
}

const sanitizePerPage = value => Math.max(parseInt(value) || DEFAULT_PER_PAGE, 1)

const sanitizeTotalRows = value => Math.max(parseInt(value) || DEFAULT_TOTAL_ROWS, 0)

const makePageArray = (startNumber, numberOfPages) =>
  Array.from({ length: numberOfPages }, (_, i) => ({
    number: startNumber + i,
    classes: null
  }))

export default {
  name: 'BPagination',
  props: {
    modelValue: {
      type: [Number, String],
      default: 1
    },
    totalRows: {
      type: Number,
      required: true
    },
    perPage: {
      type: Number,
      required: true
    },
    loading: Boolean,
    align: { type: String, default: 'left' },
    ariaLabel: { type: String, default: 'Pagination' },
    disabled: Boolean,
    ellipsisClass: { type: String, default: 'ellipsis' },
    ellipsisText: { type: String, default: '\u2026' }, // '…'
    firstClass: { type: String, default: 'first-btn' },
    firstNumber: Boolean,
    firstText: { type: String, default: '\u00AB' }, // '«'
    hideEllipsis: Boolean,
    hideGotoEndButtons: Boolean,
    labelFirstPage: { type: String, default: 'Go to first page' },
    labelLastPage: { type: String, default: 'Go to last page' },
    labelNextPage: { type: String, default: 'Go to next page' },
    labelPage: { type: String, default: 'Go to page' },
    labelPrevPage: { type: String, default: 'Go to previous page' },
    lastClass: { type: String, default: 'last-btn' },
    lastNumber: Boolean,
    lastText: { type: String, default: '\u00BB' }, // '»'
    limit: {
      type: [Number, String],
      default: 5,
      required: false,
      validator: value => {
        if (parseInt(value, 0) < 1) {
          console.warn('Prop "limit" must be a number greater than "0"')
          return false
        }
        return true
      }
    },
    nextClass: { type: String, default: 'next-btn' },
    nextText: { type: String, default: '\u203A' }, // '›'
    pageClass: { type: String, default: 'page-btn' },
    pills: Boolean,
    prevClass: { type: String, default: 'prev-btn' },
    prevText: { type: String, default: '\u2039' }, // '‹'
    size: { type: String, default: undefined },
    variant: {
      type: String,
      default: VALID_VARIANTS.light,
      validator: (value) => {
        return Object.values(VALID_VARIANTS).includes(value)
      }
    }
  },
  emits: ['change', 'update:modelValue'],
  data() {
    // `-1` signifies no page initially selected
    let currentPage = parseInt(this.modelValue, 0)
    currentPage = currentPage > 0 ? currentPage : -1
    return {
      currentPage,
      localNumberOfPages: 1,
      localLimit: DEFAULT_LIMIT,
      showFirstDots: false,
      showLastDots: false
    }
  },
  computed: {
    btnSize() {
      const { size } = this
      return size ? `pagination-${size}` : ''
    },
    skipFirst() {
      return this.firstNumber
    },
    alignment() {
      const { align } = this
      if (align === 'center') {
        return 'justify-content-center'
      } else if (align === 'end' || align === 'right') {
        return 'justify-content-end'
      } else if (align === 'fill') {
        // The page-items will also have 'flex-fill' added
        // We add text centering to make the button appearance better in fill mode
        return 'text-center'
      }
      return ''
    },
    styleClass() {
      return this.pills ? 'b-pagination-pills' : ''
    },
    computedCurrentPage() {
      return sanitizeCurrentPage(this.currentPage, this.localNumberOfPages)
    },
    numberOfPages() {
      const result = Math.ceil(sanitizeTotalRows(this.totalRows) / sanitizePerPage(this.perPage))
      return result < 1 ? 1 : result
    },
    // Used for watching changes to `perPage` and `numberOfPages`
    pageSizeNumberOfPages() {
      return {
        perPage: sanitizePerPage(this.perPage),
        totalRows: sanitizeTotalRows(this.totalRows),
        numberOfPages: this.numberOfPages
      }
    },
    paginationParams() {
      // Determine if we should show the the ellipsis
      const {
        localLimit: limit,
        localNumberOfPages: numberOfPages,
        computedCurrentPage: currentPage,
        hideEllipsis,
        firstNumber,
        lastNumber
      } = this
      let showFirstDots = false
      let showLastDots = false
      let numberOfLinks = limit
      let startNumber = 1

      if (numberOfPages <= limit) {
        // Special case: Less pages available than the limit of displayed pages
        numberOfLinks = numberOfPages
      } else if (currentPage < limit - 1 && limit > ELLIPSIS_THRESHOLD) {
        if (!hideEllipsis || lastNumber) {
          showLastDots = true
          numberOfLinks = limit - (firstNumber ? 0 : 1)
        }
        numberOfLinks = Math.min(numberOfLinks, limit)
      } else if (numberOfPages - currentPage + 2 < limit && limit > ELLIPSIS_THRESHOLD) {
        if (!hideEllipsis || firstNumber) {
          showFirstDots = true
          numberOfLinks = limit - (lastNumber ? 0 : 1)
        }
        startNumber = numberOfPages - numberOfLinks + 1
      } else {
        // We are somewhere in the middle of the page list
        if (limit > ELLIPSIS_THRESHOLD) {
          numberOfLinks = limit - (hideEllipsis ? 0 : 2)
          showFirstDots = !!(!hideEllipsis || firstNumber)
          showLastDots = !!(!hideEllipsis || lastNumber)
        }
        startNumber = currentPage - Math.floor(numberOfLinks / 2)
      }
      // Sanity checks
      if (startNumber < 1) {
        startNumber = 1
        showFirstDots = false
      } else if (startNumber > numberOfPages - numberOfLinks) {
        startNumber = numberOfPages - numberOfLinks + 1
        showLastDots = false
      }
      if (showFirstDots && firstNumber && startNumber < 4) {
        numberOfLinks = numberOfLinks + 2
        startNumber = 1
        showFirstDots = false
      }
      const lastPageNumber = startNumber + numberOfLinks - 1
      if (showLastDots && lastNumber && lastPageNumber > numberOfPages - 3) {
        numberOfLinks = numberOfLinks + (lastPageNumber === numberOfPages - 2 ? 2 : 3)
        showLastDots = false
      }
      // Special handling for lower limits (where ellipsis are never shown)
      if (limit <= ELLIPSIS_THRESHOLD) {
        if (firstNumber && startNumber === 1) {
          numberOfLinks = Math.min(numberOfLinks + 1, numberOfPages, limit + 1)
        } else if (lastNumber && numberOfPages === startNumber + numberOfLinks - 1) {
          startNumber = Math.max(startNumber - 1, 1)
          numberOfLinks = Math.min(numberOfPages - startNumber + 1, numberOfPages, limit + 1)
        }
      }
      numberOfLinks = Math.min(numberOfLinks, numberOfPages - startNumber + 1)
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.showFirstDots = showFirstDots
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.showLastDots = showLastDots
      return { showFirstDots, showLastDots, numberOfLinks, startNumber }
    },
    pageList() {
      // Generates the pageList array
      const { numberOfLinks, startNumber } = this.paginationParams
      const currentPage = this.computedCurrentPage
      // Generate list of page numbers
      const pages = makePageArray(startNumber, numberOfLinks)
      // We limit to a total of 3 page buttons on XS screens
      // So add classes to page links to hide them for XS breakpoint
      // Note: Ellipsis will also be hidden on XS screens
      // TODO: Make this visual limit configurable based on breakpoint(s)
      if (pages.length > 3) {
        const idx = currentPage - startNumber
        // THe following is a bootstrap-vue custom utility class
        const classes = 'bv-d-xs-down-none'
        if (idx === 0) {
          // Keep leftmost 3 buttons visible when current page is first page
          for (let i = 3; i < pages.length; i++) {
            pages[i].classes = classes
          }
        } else if (idx === pages.length - 1) {
          // Keep rightmost 3 buttons visible when current page is last page
          for (let i = 0; i < pages.length - 3; i++) {
            pages[i].classes = classes
          }
        } else {
          // Hide all except current page, current page - 1 and current page + 1
          for (let i = 0; i < idx - 1; i++) {
            // hide some left button(s)
            pages[i].classes = classes
          }
          for (let i = pages.length - 1; i > idx + 1; i--) {
            // hide some right button(s)
            pages[i].classes = classes
          }
        }
      }
      return pages
    }
  },
  watch: {
    pageSizeNumberOfPages(newValue, oldValue) {
      if (oldValue !== null || oldValue !== undefined) {
        if (newValue.perPage !== oldValue.perPage && newValue.totalRows === oldValue.totalRows) {
          // If the page size changes, reset to page 1
          this.currentPage = 1
        } else if (
          newValue.numberOfPages !== oldValue.numberOfPages && this.currentPage > newValue.numberOfPages
        ) {
          // If `numberOfPages` changes and is less than
          // the `currentPage` number, reset to page 1
          this.currentPage = 1
        }
      }
      this.localNumberOfPages = newValue.numberOfPages
    },
    modelValue(n, o) {
      if (n !== o) {
        this.currentPage = n
      }
    }
  },
  created() {
    // Set our default values in data
    this.localLimit = sanitizeLimit(this.limit)
    // Set the initial page count
    this.localNumberOfPages = this.numberOfPages
    // Set the initial page value
    const currentPage = parseInt(this.modelValue, 0)
    if (currentPage > 0) {
      this.currentPage = currentPage
    } else {
      this.$nextTick(() => {
        // If this value parses to `NaN` or a value less than `1`
        // trigger an initial emit of `null` if no page specified
        this.currentPage = 0
      })
    }
  },
  methods: {
    onClick(_, pageNumber) {
      if (pageNumber === this.currentPage) {
        return
      }
      this.currentPage = pageNumber
      this.$emit('change', this.currentPage)
      this.$emit('update:modelValue', this.currentPage)
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@ui/styles/src/scss/utils/lib";

$modes-colors: (
  'dark': (
    'color': $grey-65,
    'hoverColor': #fff,
    'hoverBackground': $grey-40,
    'hoverBorderColor': $grey-40,
    'disabledColor': $grey-50,
    'activeColor': #fff,
    'activeBackground': #{$lightPrimaryColor}40,
    'activeBorderColor': #{$lightPrimaryColor}60
  ),
  'light': (
    'color': $darkPrimaryColor,
    'hoverBackground': #{$grey-30}10,
    'hoverBorderColor': #{$grey-30}10,
    'hoverColor': $darkPrimaryColor,
    'disabledColor': $grey-50,
    'activeColor': #fff,
    'activeBackground': $darkPrimaryColor,
    'activeBorderColor': #{$blue-90}80
  )
);

@function map-deep-get($map, $keys...) {
  @each $key in $keys {
    $map: map-get($map, $key);
  }

  @return $map;
}

@mixin commonStyles {
  background: none;
  border-color: transparent;
}

:deep(.ellipsis),
:deep(.first-btn),
:deep(.last-btn),
:deep(.page-btn),
:deep(.next-btn),
:deep(.prev-btn) {
  margin: 0 0.25rem;

  span.page-link,
  button.page-link {
    @include commonStyles;

    padding: 0.2rem 0.4rem;

    @each $mode, $props in $modes-colors {
      .pagination--#{$mode} > & {
        color: map-deep-get($modes-colors, $mode, 'color');
      }
    }

    &:hover,
    &:focus {
      box-shadow: none;

      @each $mode, $props in $modes-colors {
        .pagination--#{$mode} > & {
          color: map-deep-get($modes-colors, $mode, 'hoverColor');
          background-color: map-deep-get($modes-colors, $mode, 'hoverBackground');
          border-color: map-deep-get($modes-colors, $mode, 'hoverBorderColor');
        }
      }
    }
  }

  &.disabled {
    span.page-link,
    button.page-link {
      @include commonStyles;

      @each $mode, $props in $modes-colors {
        .pagination--#{$mode} > & {
          color: map-deep-get($modes-colors, $mode, 'disabledColor');
        }
      }
    }
  }
}

:deep(.page-btn) {
  &.active {
    button.page-link {
      @each $mode, $props in $modes-colors {
        .pagination--#{$mode} > & {
          color: map-deep-get($modes-colors, $mode, 'activeColor');
          background-color: map-deep-get($modes-colors, $mode, 'activeBackground');
          border-color: map-deep-get($modes-colors, $mode, 'activeBorderColor');
        }
      }
    }
  }
}
</style>

